/* "Which album should this go in?" — one choice over a flat list.
 *
 * The manager navigates a tree because that is how someone browses their own
 * photos. Filing one is not browsing: it is a single decision, and making
 * someone walk gallery → album → sub-album to express it would be three clicks
 * for one answer. So the tree is flattened and each option says where it sits.
 *
 * Sub-albums are offered too. A sub-album is a perfectly good destination — it
 * is where "Trex" lives under "Decking" — and leaving them out would push
 * everything up a level.
 */
import { useQuery } from '@tanstack/react-query';
import { Select } from '@/components/ui';
import { getGalleryAlbums, type FlatAlbum } from '@/data/client';

/** One album's label, saying enough to tell it from a namesake elsewhere. */
export function albumLabel(a: FlatAlbum, showGallery: boolean): string {
  const path = a.parentName ? `${a.parentName} › ${a.name}` : a.name;
  return showGallery && a.galleryName ? `${a.galleryName} · ${path}` : path;
}

export function useAlbums() {
  const { data, isFetching } = useQuery({
    queryKey: ['galleryAlbums'], queryFn: getGalleryAlbums, staleTime: 60_000,
  });
  const albums = data?.albums ?? [];
  return {
    albums,
    /* Naming the gallery only helps when there is more than one; on the usual
       single-gallery client it is a word repeated down the whole list. */
    showGallery: new Set(albums.map((a) => a.galleryName)).size > 1,
    loading: isFetching && !data,
  };
}

export function AlbumSelect({ value, onChange, placeholder = 'Don’t add to an album' }: {
  /** Album id, or '' for none. */
  value: string;
  onChange: (albumId: string) => void;
  placeholder?: string;
}) {
  const { albums, showGallery, loading } = useAlbums();

  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={[
        { value: '', label: loading ? 'Loading albums…' : albums.length ? placeholder : 'No albums yet' },
        ...albums.map((a) => ({ value: a.id, label: albumLabel(a, showGallery) })),
      ]}
    />
  );
}
